import type { MapTileDataJson } from '../platform/api';

const DB_NAME = 'mytile-map-cache';
const DB_VERSION = 2;
const STORE = 'tiles';
const META_STORE = 'meta';
const MAX_BYTES = 200 * 1024 * 1024;
const EVICT_DEBOUNCE_MS = 30_000;

interface CacheEntry {
  key: string;
  data: MapTileDataJson[][];
}

interface MetaEntry {
  key: string;
  accessedAt: number;
  sizeEstimate: number;
}

export interface MapCache {
  get(key: string): Promise<MapTileDataJson[][] | null>;
  put(key: string, data: MapTileDataJson[][]): Promise<void>;
  close(): void;
}

function estimateSize(data: MapTileDataJson[][]): number {
  if (data.length === 0) return 2;
  const rows = data.length;
  const cols = data[0]?.length ?? 0;
  return rows * cols * 130;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        const meta = db.createObjectStore(META_STORE, { keyPath: 'key' });
        meta.createIndex('accessedAt', 'accessedAt');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function openMapCache(): Promise<MapCache> {
  let db: IDBDatabase;
  try {
    db = await openDB();
  } catch {
    return nullCache();
  }

  let evictTimer: ReturnType<typeof setTimeout> | null = null;

  function scheduleEvict(): void {
    if (evictTimer) return;
    evictTimer = setTimeout(() => {
      evictTimer = null;
      evictIfNeeded(db).catch(() => {});
    }, EVICT_DEBOUNCE_MS);
  }

  return {
    async get(key: string): Promise<MapTileDataJson[][] | null> {
      try {
        const tx = db.transaction(STORE, 'readonly');
        const req = tx.objectStore(STORE).get(key);
        const entry = await new Promise<CacheEntry | undefined>((resolve, reject) => {
          req.onsuccess = () => resolve(req.result as CacheEntry | undefined);
          req.onerror = () => reject(req.error);
        });
        if (!entry) return null;
        touchMeta(db, key).catch(() => {});
        return entry.data;
      } catch {
        return null;
      }
    },

    async put(key: string, data: MapTileDataJson[][]): Promise<void> {
      try {
        const sizeEstimate = estimateSize(data);
        const tx = db.transaction([STORE, META_STORE], 'readwrite');
        tx.objectStore(STORE).put({ key, data });
        tx.objectStore(META_STORE).put({ key, accessedAt: Date.now(), sizeEstimate });
        await new Promise<void>((resolve, reject) => {
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
        });
        scheduleEvict();
      } catch {
        // Cache is best-effort
      }
    },

    close() {
      if (evictTimer) { clearTimeout(evictTimer); evictTimer = null; }
      db.close();
    },
  };
}

function nullCache(): MapCache {
  return {
    async get() { return null; },
    async put() {},
    close() {},
  };
}

async function touchMeta(db: IDBDatabase, key: string): Promise<void> {
  try {
    const tx = db.transaction(META_STORE, 'readwrite');
    const store = tx.objectStore(META_STORE);
    const req = store.get(key);
    const existing = await new Promise<MetaEntry | undefined>((resolve) => {
      req.onsuccess = () => resolve(req.result as MetaEntry | undefined);
      req.onerror = () => resolve(undefined);
    });
    if (existing) {
      existing.accessedAt = Date.now();
      store.put(existing);
    }
  } catch {
    // best-effort
  }
}

async function evictIfNeeded(db: IDBDatabase): Promise<void> {
  const tx = db.transaction(META_STORE, 'readonly');
  const store = tx.objectStore(META_STORE);

  let totalSize = 0;
  const entries: MetaEntry[] = [];

  await new Promise<void>((resolve, reject) => {
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (cursor) {
        const e = cursor.value as MetaEntry;
        totalSize += e.sizeEstimate;
        entries.push(e);
        cursor.continue();
      } else {
        resolve();
      }
    };
    req.onerror = () => reject(req.error);
  });

  if (totalSize <= MAX_BYTES) return;

  entries.sort((a, b) => a.accessedAt - b.accessedAt);

  const toDelete: string[] = [];
  while (totalSize > MAX_BYTES && entries.length > 0) {
    const oldest = entries.shift()!;
    toDelete.push(oldest.key);
    totalSize -= oldest.sizeEstimate;
  }

  if (toDelete.length === 0) return;

  const delTx = db.transaction([STORE, META_STORE], 'readwrite');
  const delStore = delTx.objectStore(STORE);
  const delMeta = delTx.objectStore(META_STORE);
  for (const key of toDelete) {
    delStore.delete(key);
    delMeta.delete(key);
  }
  await new Promise<void>((resolve) => {
    delTx.oncomplete = () => resolve();
    delTx.onerror = () => resolve();
  });
}

export function idbKey(mw: string, genId: string, cellX: number, cellY: number): string {
  return `${mw}:${genId}:${cellX}:${cellY}`;
}
